import {
  Award,
  ClipboardCheck,
  ClipboardList,
  FilePlus2,
  FlaskConical,
  HeartPulse,
  History,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  MessageSquareText,
  NotebookPen,
  Paperclip,
  Pill,
  ScanLine,
  Scissors,
  ShieldAlert,
  Stethoscope,
  Tablets,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  emrContextOfEncounter,
  emrFormDefinition,
  emrFormFilled,
  isClinicalFormCode,
  isEmrFormKey,
  rosFindingsRecorded,
  type ClinicalFormCode,
  type EmrCompletionFacts,
  type ResolvedEmrForm,
} from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { type Encounter } from './types';
import { completionFactsOf, requiredFormsApply } from './completion';
import { orderLineCount } from './orders/summary';

export const EMR_SECTION_IDS = [
  'overview',
  'vitals',
  'complaint',
  'ros',
  'examination',
  'diagnosis',
  'plan',
  'prescription',
  'lab_order',
  'radiology_order',
  'procedure_order',
  'allergies',
  'medicines',
  'problems',
  'history',
  'files',
  'certificates',
  'care-team',
  'addenda',
] as const;
export type EmrSectionId = (typeof EMR_SECTION_IDS)[number];
export type EmrNavId = EmrSectionId | ClinicalFormCode;

export type EmrGroup = 'visit' | 'orders' | 'forms' | 'record' | 'documents';

export interface EmrSection {
  id: EmrNavId;
  group: EmrGroup;
  label: string;
  icon: LucideIcon;
  count?: number;
  done?: boolean;
  required?: boolean;
}

export interface NoteFieldDef {
  key: string;
  label: string;
  rows: number;
  wide?: boolean;
}

export function historyFields(): NoteFieldDef[] {
  return [
    { key: 'presentingComplaints', label: tr('opd', 'note_presenting_complaints'), rows: 3, wide: true },
    { key: 'historyOfPresentIllness', label: tr('opd', 'note_history_of_present_illness'), rows: 5, wide: true },
    { key: 'pastHistory', label: tr('opd', 'note_past_history'), rows: 2 },
    { key: 'surgicalHistory', label: tr('opd', 'note_surgical_history'), rows: 2 },
    { key: 'familyHistory', label: tr('opd', 'note_family_history'), rows: 2 },
    { key: 'socialHistory', label: tr('opd', 'note_social_history'), rows: 2 },
    { key: 'drugHistory', label: tr('opd', 'note_drug_history'), rows: 2, wide: true },
  ];
}

export function examinationFields(): NoteFieldDef[] {
  return [{ key: 'examination', label: tr('opd', 'note_examination'), rows: 8, wide: true }];
}

export function planFields(): NoteFieldDef[] {
  return [
    { key: 'treatmentPlan', label: tr('opd', 'note_plan'), rows: 4, wide: true },
    { key: 'advice', label: tr('opd', 'note_advice'), rows: 3, wide: true },
  ];
}

export const GROUP_ORDER: EmrGroup[] = ['visit', 'orders', 'forms', 'record', 'documents'];

export function groupLabel(group: EmrGroup): string {
  return tr('opd', `emr_group_${group}`);
}

export function rosRecorded(encounter: Encounter): boolean {
  return rosFindingsRecorded(encounter.notes);
}

const SECTION_FORM_KEYS: Readonly<Record<string, string>> = { complaint: 'complaint_history', ros: 'review_of_systems', plan: 'plan_follow_up' };

export function formKeyOfSection(id: string): string | null {
  const key = SECTION_FORM_KEYS[id] ?? id;
  return isEmrFormKey(key) ? key : null;
}

export function sectionOfFormKey(key: string): EmrNavId {
  const section = Object.keys(SECTION_FORM_KEYS).find((id) => SECTION_FORM_KEYS[id] === key);
  return (section ?? key) as EmrNavId;
}

function clinicalFormSections(forms: readonly ResolvedEmrForm[]): EmrSection[] {
  return forms.flatMap((form): EmrSection[] =>
    form.kind === 'clinical_form' && form.enabled && isClinicalFormCode(form.key)
      ? [{ id: form.key, group: 'forms', label: tr('opd', `emr_form_${form.key}`), icon: ClipboardList }]
      : [],
  );
}

function arranged(sections: readonly EmrSection[], encounter: Encounter, forms: readonly ResolvedEmrForm[], facts: EmrCompletionFacts): EmrSection[] {
  const context = emrContextOfEncounter(encounter.context);
  const byKey = new Map(forms.map((form) => [form.key, form]));
  const markRequired = requiredFormsApply(encounter);
  const configured = (section: EmrSection): ResolvedEmrForm | null | undefined => {
    const key = formKeyOfSection(section.id);
    if (!key || !emrFormDefinition(key)?.contexts.includes(context)) return undefined;
    return byKey.get(key) ?? null;
  };
  const kept = sections.flatMap((section): EmrSection[] => {
    const form = configured(section);
    if (form === undefined) return [section];
    if (form === null || !form.enabled) return [];
    return [{ ...section, done: emrFormFilled(form.key, facts), required: markRequired && form.required }];
  });
  const sortOrder = (section: EmrSection) => configured(section)?.sortOrder ?? 0;
  return GROUP_ORDER.flatMap((group) => {
    const items = kept.filter((section) => section.group === group);
    const movable = items.filter((section) => configured(section) !== undefined).sort((a, b) => sortOrder(a) - sortOrder(b));
    let next = 0;
    return items.map((section) => (configured(section) === undefined ? section : (movable[next++] ?? section)));
  });
}

export function emrSections(encounter: Encounter, options: { closed: boolean; forms?: readonly ResolvedEmrForm[] }): EmrSection[] {
  const facts = completionFactsOf(encounter);
  const filled = (key: string) => emrFormFilled(key, facts);
  const sections: (EmrSection | false)[] = [
    { id: 'overview', group: 'visit', label: tr('opd', 'emr_summary'), icon: LayoutDashboard },
    { id: 'vitals', group: 'visit', label: tr('opd', 'emr_vitals'), icon: HeartPulse, count: encounter.vitals.length, done: filled('vitals') },
    { id: 'complaint', group: 'visit', label: tr('opd', 'emr_complaint_history'), icon: MessageSquareText, done: filled('complaint_history') },
    { id: 'ros', group: 'visit', label: tr('opd', 'review_of_systems'), icon: ListChecks, done: filled('review_of_systems') },
    { id: 'examination', group: 'visit', label: tr('opd', 'emr_examination'), icon: Stethoscope, done: filled('examination') },
    { id: 'diagnosis', group: 'visit', label: tr('opd', 'diagnosis'), icon: ClipboardCheck, count: encounter.diagnoses.length, done: filled('diagnosis') },
    { id: 'plan', group: 'visit', label: tr('opd', 'emr_plan_follow_up'), icon: NotebookPen, done: filled('plan_follow_up') },
    { id: 'prescription', group: 'orders', label: tr('opd', 'prescription'), icon: Pill, count: encounter.prescriptions.length, done: filled('prescription') },
    { id: 'lab_order', group: 'orders', label: tr('opd', 'emr_form_lab_order'), icon: FlaskConical, count: orderLineCount(encounter, 'lab') },
    { id: 'radiology_order', group: 'orders', label: tr('opd', 'emr_form_radiology_order'), icon: ScanLine, count: orderLineCount(encounter, 'radiology') },
    { id: 'procedure_order', group: 'orders', label: tr('opd', 'emr_form_procedure_order'), icon: Scissors, count: orderLineCount(encounter, 'procedure') },
    { id: 'allergies', group: 'record', label: tr('opd', 'allergies'), icon: ShieldAlert, count: encounter.patient.allergies.length },
    { id: 'medicines', group: 'record', label: tr('opd', 'emr_current_medicines'), icon: Tablets },
    { id: 'problems', group: 'record', label: tr('opd', 'problem_list'), icon: ListTodo },
    { id: 'history', group: 'record', label: tr('opd', 'previous_visits'), icon: History },
    { id: 'files', group: 'documents', label: tr('opd', 'attachments'), icon: Paperclip },
    { id: 'certificates', group: 'documents', label: tr('opd', 'certificates'), icon: Award },
    Boolean(encounter.appointmentId) && {
      id: 'care-team',
      group: 'documents',
      label: tr('opd', 'emr_care_team'),
      icon: Users,
    },
    options.closed && { id: 'addenda', group: 'documents', label: tr('opd', 'addenda'), icon: FilePlus2, count: encounter.addenda.length },
  ];
  const present = sections.filter((section): section is EmrSection => Boolean(section));
  if (!options.forms) return present;
  return arranged([...present, ...clinicalFormSections(options.forms)], encounter, options.forms, facts);
}

export function sectionFromParams(params: URLSearchParams, available: readonly EmrSection[]): EmrNavId {
  const wanted = params.get('section') ?? params.get('tab');
  const legacy: Record<string, EmrSectionId> = { note: 'complaint', rx: 'prescription', diagnosis: 'diagnosis', orders: 'lab_order', files: 'files', certificates: 'certificates', history: 'history' };
  const id = wanted ? (legacy[wanted] ?? wanted) : 'overview';
  return available.find((section) => section.id === id)?.id ?? 'overview';
}
